/**
 * Newsletter Archive Block
 * 
 * Handles PDF viewer switching functionality for the newsletter archive block.
 * Uses browser's native PDF viewer via iframe for reliable cross-browser support.
 */

import { queryAll, query } from "../utils/utils";

export class NewsletterArchive {
    constructor() {
        this.block = query('.newsletter-archive-block');
        
        if (this.block) {
            this.init();
        }
    }

    init() {
        this.viewButtons = queryAll('.newsletter-view-btn', this.block);
        this.newsletterItems = queryAll('.newsletter-item', this.block);
        this.viewer = query('.newsletter-viewer', this.block);
        
        this.bindEvents();
    }

    bindEvents() {
        this.viewButtons.forEach(btn => {
            btn.addEventListener('click', (e) => this.handleViewClick(e));
        });
    }

    handleViewClick(e) {
        e.preventDefault();
        
        const button = e.currentTarget;
        const pdfUrl = button.dataset.pdfUrl;
        const newsletterId = button.dataset.newsletterId;
        
        if (!pdfUrl) {
            console.warn('[Newsletter Archive] No PDF URL found');
            return;
        }

        // Update active state on newsletter items
        this.updateActiveState(newsletterId);
        
        // Load the PDF
        this.loadPdf(pdfUrl);
        
        // Scroll to viewer
        this.scrollToViewer();
    }

    updateActiveState(newsletterId) {
        // Remove active class from all items
        this.newsletterItems.forEach(item => {
            item.classList.remove('active');
        });
        
        // Add active class to selected item
        const selectedItem = query(`.newsletter-item[data-newsletter-id="${newsletterId}"]`, this.block);
        if (selectedItem) {
            selectedItem.classList.add('active');
        }
    }

    loadPdf(pdfUrl) {
        if (!this.viewer) {
            console.warn('[Newsletter Archive] Viewer element not found');
            return;
        }

        // Show loading state
        this.viewer.classList.add('loading');

        // Find existing iframe or create new one
        let iframe = query('.pdf-iframe', this.viewer);
        
        if (iframe) {
            // Update existing iframe src
            iframe.src = pdfUrl;
        } else {
            // Create new iframe
            iframe = document.createElement('iframe');
            iframe.className = 'pdf-iframe';
            iframe.title = 'Newsletter PDF Viewer';
            iframe.setAttribute('allowfullscreen', '');
            iframe.src = pdfUrl;
            
            this.viewer.innerHTML = '';
            this.viewer.appendChild(iframe);
        }

        // Remove loading state when iframe loads
        iframe.onload = () => {
            this.viewer.classList.remove('loading');
        };

        // Fallback: remove loading after timeout in case onload doesn't fire
        setTimeout(() => {
            this.viewer.classList.remove('loading');
        }, 3000);
    }

    scrollToViewer() {
        const viewerContainer = query('.newsletter-viewer-container', this.block);
        if (viewerContainer) {
            // Add offset for fixed header if present
            const headerHeight = query('header')?.offsetHeight || 0;
            const targetPosition = viewerContainer.getBoundingClientRect().top + window.pageYOffset - headerHeight - 20;
            
            window.scrollTo({ 
                top: targetPosition,
                behavior: 'smooth'
            });
        }
    }
}
